"use client";

import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { useSnackbar } from "react-simple-snackbar";

import styles from "./notification.module.css";

import { useSidebarContext } from "../Context";
import NotificationMenu from "./NotificationMenu";
import * as apiClient from "../../../../../common/apiClient";
import * as apiMethod from "../../../../../common/apiMethod";
import * as appModule from "../../../../../common/appModule";
import options from "./snackbarOptions";
import { useSeenNotifications } from "./useSeenNotifications";

const POLL_INTERVAL = 10000; // 10 seconds

const Notification = () => {
  const { isSidebarCollapsed } = useSidebarContext();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [openSnackbar] = useSnackbar(options);

  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const previousRef = useRef([]);

  const toggleNotification = () => setIsOpen((prev) => !prev);
  const closeNotification = () => setIsOpen(false);

  const hasNotifications =
    Array.isArray(notifications) && notifications.length > 0;
  const showHeading = !isSidebarCollapsed && "Notifications";

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeNotification();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Handle Esc key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        closeNotification();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Load task queue
  const { getSeenNotifications, markAsSeen, syncSeenWithServer } =
    useSeenNotifications();

  const loadTaskQueue = async () => {
    try {
      const response = await apiClient.get(
        `${apiClient.baseUrl}${appModule.tenancyModule}${apiMethod.taskqueue}`,
        null
      );

      const result = response?.ResultSet || [];
      const seenIds = getSeenNotifications();
      const currentIds = result.map((n) => n.TaskId);

      // Find unseen notifications
      const unseen = result.find((n) => !seenIds.includes(n.TaskId));

      if (unseen) {
        openSnackbar(`${unseen.TaskName} ${unseen.TaskStatus}`);
        markAsSeen(unseen.TaskId);
      }

      // Now sync localStorage to remove stale task IDs
      syncSeenWithServer(currentIds);

      setNotifications(result);
    } catch (err) {
      console.error("Failed to load notifications", err);
    }
  };

  // Initial fetch + polling
  useEffect(() => {
    loadTaskQueue(); // initial fetch

    const interval = setInterval(() => {
      loadTaskQueue();
    }, POLL_INTERVAL);

    return () => clearInterval(interval); // cleanup
  }, []);

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        isSidebarCollapsed ? styles.collapsed : ""
      }`}
      onClick={toggleNotification}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleNotification();
        }
      }}
      tabIndex={0}
      role="button"
      aria-haspopup="true"
      aria-expanded={isOpen}
      aria-label="Toggle notification menu"
    >
      <h3 className={styles.notification_text}>
        <FontAwesomeIcon icon={faBell} />
        {showHeading}
        {hasNotifications && (
          <span
            className={`${styles.notification_count} ${
              isSidebarCollapsed ? styles.count_collapsed : ""
            }`}
          >
            {notifications.length}
          </span>
        )}
      </h3>

      {isOpen && (
        <NotificationMenu
          ref={menuRef}
          notifications={notifications}
          onClose={closeNotification}
        />
      )}
    </div>
  );
};

export default Notification;
